import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Chip,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { INTROSPECT_TABLES } from 'src/utils/gql/queries';

interface IntrospectionTableProps {
  connectionId: string;
  onTablesSelected: (tables: { schema: string; tableName: string }[]) => void;
  selectedTables?: { schema: string; tableName: string }[];
}

export const IntrospectionTable: React.FC<IntrospectionTableProps> = ({
  connectionId,
  onTablesSelected,
  selectedTables = [],
}) => {
  const [selectedTablesLocal, setSelectedTablesLocal] =
    useState<any[]>(selectedTables);

  console.log('IntrospectionTable - connectionId:', connectionId);

  const { data, loading, error } = useQuery(INTROSPECT_TABLES, {
    variables: { connectionId },
    skip: !connectionId,
  });

  console.log(
    'IntrospectionTable - loading:',
    loading,
    'error:',
    error,
    'data:',
    data,
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Errore nel caricamento delle tabelle: {error.message}
      </Alert>
    );
  }

  const tables = data?.introspectTables || [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        📋 Seleziona le tabelle dal database
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Trovate <strong>{tables.length}</strong> tabelle disponibili •
          Selezionate: <strong>{selectedTablesLocal.length}</strong>
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const commonTables = tables.filter((table: any) =>
                [
                  'users',
                  'companies',
                  'chatgpt_usage_logs',
                  'conversations',
                ].includes(table.tableName),
              );
              setSelectedTablesLocal(commonTables);
            }}
            disabled={selectedTablesLocal.length === 4}
          >
            Tabelle Comuni
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedTablesLocal(tables)}
            disabled={selectedTablesLocal.length === tables.length}
          >
            Seleziona Tutte
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedTablesLocal([])}
            disabled={selectedTablesLocal.length === 0}
          >
            Deseleziona Tutte
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ maxHeight: 400, overflow: 'auto' }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  width: 50,
                }}
              >
                ✓
              </TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
              >
                Schema
              </TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
              >
                Tabella
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  width: 120,
                }}
              >
                Azioni
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tables.map((table: any, index: number) => {
              const isSelected = selectedTablesLocal.some(
                (selected) =>
                  selected.schema === table.schema &&
                  selected.tableName === table.tableName,
              );

              return (
                <TableRow
                  key={index}
                  sx={{
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#e3f2fd' : 'inherit',
                  }}
                  onClick={() => {
                    if (isSelected) {
                      const newSelected = selectedTablesLocal.filter(
                        (selected) =>
                          !(
                            selected.schema === table.schema &&
                            selected.tableName === table.tableName
                          ),
                      );
                      setSelectedTablesLocal(newSelected);
                    } else {
                      setSelectedTablesLocal([...selectedTablesLocal, table]);
                    }
                  }}
                >
                  <TableCell>
                    {isSelected && (
                      <Box
                        sx={{
                          color: 'primary.main',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        ✓
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', color: '#666' }}
                    >
                      {table.schema}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {table.tableName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant={isSelected ? 'outlined' : 'contained'}
                      size="small"
                      color={isSelected ? 'error' : 'primary'}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelected) {
                          const newSelected = selectedTablesLocal.filter(
                            (selected) =>
                              !(
                                selected.schema === table.schema &&
                                selected.tableName === table.tableName
                              ),
                          );
                          setSelectedTablesLocal(newSelected);
                        } else {
                          setSelectedTablesLocal([
                            ...selectedTablesLocal,
                            table,
                          ]);
                        }
                      }}
                    >
                      {isSelected ? '❌ Deseleziona' : '✅ Seleziona'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {tables.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nessuna tabella trovata nel database
        </Alert>
      )}

      {selectedTablesLocal.length > 0 && (
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Tabelle selezionate: <strong>{selectedTablesLocal.length}</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedTablesLocal.map((table, index) => (
                <Chip
                  key={index}
                  label={`${table.schema}.${table.tableName}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={() => onTablesSelected(selectedTablesLocal)}
          >
            Continua con {selectedTablesLocal.length} tabella
            {selectedTablesLocal.length > 1 ? 'e' : ''} →
          </Button>
        </Box>
      )}
    </Box>
  );
};
